import React, {useCallback, useEffect, useState} from "react"
import {AddCircleOutline, ArticleOutlined, ArrowBack} from "@mui/icons-material"
import TemplateForm from "./TemplateForm";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";
import {useSiren} from "../SirenContext";
import {DeliveryMethod, MassWarningTemplate} from "../typedefs";
import {useSearchParams} from "react-router-dom";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {platformAPI} from "../../../api/axiosConfig";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {useGetMassWarningTemplates} from "../hooks/useGetMassWarningTemplates";
import {usePermissions} from "@/context/modulePermissions";


function getUserIDs(uuid: string | null): string[] {
    if (!uuid) {
        return []
    }
    const json = localStorage.getItem(`mass-warning-users-${uuid}`);
    if (json) {
        return JSON.parse(json);
    }
    return []
}

function getContactIDs(uuid: string | null): string[] {
    if (!uuid) {
        return []
    }
    const json = localStorage.getItem(`mass-warning-contacts-${uuid}`);
    if (json) {
        return JSON.parse(json);
    }
    return []
}

function formatMethod(method: string): string {
    return method
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\bSms\b/g, "SMS")
}

function DeliveryMethodPills({methods}: {methods: DeliveryMethod[]}) {
    const [expanded, setExpanded] = useState(false)

    if (methods.length === 0) return <span className="text-grey text-sm">—</span>

    const limit = 3
    const visible = expanded ? methods : methods.slice(0, limit)
    const hidden = methods.length - limit

    return (
        <div className="flex flex-wrap gap-1 items-center" onClick={e => e.stopPropagation()}>
            {visible.map(m => (
                <span key={m} className="px-2 py-0.5 rounded-full bg-light-light-grey text-xs text-dark-dark-grey whitespace-nowrap">
                    {formatMethod(m)}
                </span>
            ))}
            {!expanded && hidden > 0 && (
                <button
                    className="px-2 py-0.5 rounded-full border border-light-grey text-xs text-grey hover:bg-light-light-grey transition-colors whitespace-nowrap"
                    onClick={() => setExpanded(true)}
                >
                    +{hidden}
                </button>
            )}
            {expanded && hidden > 0 && (
                <button
                    className="px-2 py-0.5 rounded-full border border-light-grey text-xs text-grey hover:bg-light-light-grey transition-colors"
                    onClick={() => setExpanded(false)}
                >
                    less
                </button>
            )}
        </div>
    )
}

type Step = "choose" | "select-template" | "form"

export function AlertCreate() {

    const {hasPermission} = usePermissions()
    const canUseTemplates = hasPermission("siren_resources", "read")

    const [step, setStep] = useState<Step>(canUseTemplates ? "choose" : "form")
    const [formSource, setFormSource] = useState<"choose" | "select-template">("choose")
    const [searchParams] = useSearchParams()

    const [selectedTemplate, setSelectedTemplate] = useState<MassWarningTemplate>()
    const [selectedTemplateID, setSelectedTemplateID] = useState<string[]>([])
    const {clientID} = useSiren()
    const userList = searchParams.get("userList")
    const contactList = searchParams.get("contactList")
    const templateID = searchParams.get("templateID")

    const [userIDs, setUserIDs] = useState<string[]>(getUserIDs(userList))
    const [contactIDs, setContactIDs] = useState<string[]>(getContactIDs(contactList))


    const [loading, setLoading] = useState<boolean>(!!userList)

    const getTemplates = useGetMassWarningTemplates("", clientID)

    const fetchContacts = useCallback(async () => {

        console.log(templateID)
        if (templateID) {

            if (templateID != "no_template" && canUseTemplates) {
                const res = await platformAPI.get<MassWarningTemplate>(`api/siren/templates/${templateID}`)
                setSelectedTemplate(res.data)
            }
            setStep("form")
        }


        if (contactIDs.length != 0) {
            setLoading(false)
            return
        }

        if (userIDs.length == 0) {
            setLoading(false)
            return
        }

        const res = await platformAPI.post<string[]>("api/contacts/get-by-user-id", {
            userIDs: userIDs,
            clientID: clientID,
        })

        setContactIDs(res.data)
        setLoading(false)

    }, [clientID, userIDs, templateID, canUseTemplates])

    useEffect(() => {
        fetchContacts()
    }, [])


    const handleUseTemplate = useCallback(async () => {
        if (selectedTemplateID.length === 0) return
        const id = selectedTemplateID[0]
        const res = await platformAPI.get<MassWarningTemplate>(`api/siren/templates/${id}`)
        let tmpl: MassWarningTemplate = res.data
        if (tmpl.type === "message") {
            tmpl = {...tmpl, useDefaultBroadcastConfig: true, useDefaultDeliveryMethods: true}
        }
        if (contactIDs.length > 0) {
            tmpl = {...tmpl, contactIDs, zones: [], facilityIDs: [], contactGroupIDs: []}
        }
        setSelectedTemplate(tmpl)
        setFormSource("select-template")
        setStep("form")
    }, [selectedTemplateID, contactIDs])

    if (loading) {
        return (
            <div className="flex align-center items-center justify-center bg-white shadow-md p-4  rounded-md">
                <LoadingSpinner/>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {step === "choose" && (
                <div className="bg-white shadow-md p-8 flex flex-col items-center gap-6">
                    <h2 className="text-base font-semibold text-dark-dark-grey">How would you like to create this alert?</h2>
                    <div className="flex gap-4 w-full max-w-lg">
                        <button
                            onClick={() => { setSelectedTemplate(undefined); setFormSource("choose"); setStep("form") }}
                            className="flex-1 flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-light-grey hover:border-blue hover:bg-white-blue transition-colors group"
                        >
                            <AddCircleOutline className="text-grey group-hover:text-blue transition-colors" style={{fontSize: 36}}/>
                            <span className="font-semibold text-dark-dark-grey">Create New</span>
                            <span className="text-xs text-grey text-center">Start from scratch with a blank alert</span>
                        </button>
                        {canUseTemplates && <button
                            onClick={() => setStep("select-template")}
                            className="flex-1 flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-light-grey hover:border-blue hover:bg-white-blue transition-colors group"
                        >
                            <ArticleOutlined className="text-grey group-hover:text-blue transition-colors" style={{fontSize: 36}}/>
                            <span className="font-semibold text-dark-dark-grey">Use Template</span>
                            <span className="text-xs text-grey text-center">Pre-fill from a saved template</span>
                        </button>}
                    </div>
                </div>
            )}

            {step === "select-template" && (
                <div className="bg-white shadow-md rounded-md">
                    <div className="flex items-center gap-3 p-4 border-b border-light-light-grey">
                        <button
                            onClick={() => setStep("choose")}
                            className="text-grey hover:text-dark-dark-grey transition-colors"
                        >
                            <ArrowBack style={{fontSize: 22}}/>
                        </button>
                        <h3 className="text-base font-semibold text-dark-dark-grey">Select a Template</h3>
                    </div>
                    <div className="p-4">
                        <PaginatedSelector<MassWarningTemplate>
                            headers={["Name", "Type", "Delivery Methods"]}
                            columnTemplate="minmax(0,240px) minmax(0,120px) minmax(0,2fr)"
                            getItems={getTemplates}
                            getColumns={(t) => [
                                t.name,
                                t.type ? t.type.charAt(0).toUpperCase() + t.type.slice(1) : "—",
                                t.useDefaultDeliveryMethods
                                    ? <span className="text-xs bg-global-purple text-white px-1.5 py-0.5 rounded">Default</span>
                                    : <DeliveryMethodPills methods={t.deliveryMethods ?? []} />,
                            ]}
                            selectedIDs={selectedTemplateID}
                            setSelectedIDs={(ids) => {
                                const latest = ids.filter(id => !selectedTemplateID.includes(id))
                                setSelectedTemplateID(latest.length ? [latest[latest.length - 1]] : ids.slice(-1))
                            }}
                            disableSelect={false}
                            hideSelectAll={true}
                            nonClickableColumns={[2]}
                        />
                        <div className="flex justify-end pt-2">
                            <ButtonV2
                                label="Use Template"
                                onClick={handleUseTemplate}
                                disabled={selectedTemplateID.length === 0}
                            />
                        </div>
                    </div>
                </div>
            )}

            {step === "form" && !loading && (
                <TemplateForm key={selectedTemplate?.id ?? "blank"} mode={"alert"} template={selectedTemplate} initialContactIDs={contactIDs} onBack={() => setStep(formSource)}/>
            )}
        </div>
    )
}
